import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_REASONS = [
  "",
  "hot news tip",
  "Follow up on a story we did.",
  "Incorrect reporting of something.",
  "Report a slumlord.",
  "Report a corrupt business.",
  "other",
];

export const ContactForm = ({ sourcePage = "contact", submissionType = "contact", submitLabel = "Submit", title = "Tell us what you’re reporting" }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    reason: "",
    details: "",
    allowContact: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field) => (event) => {
    const value = event.target.value;
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/submissions`, {
        submission_type: submissionType,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        reason: formData.reason,
        details: formData.details,
        allow_contact: formData.allowContact,
        source_page: sourcePage,
      });
      toast.success("Submission saved for the newsroom.");
      setFormData({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        reason: "",
        details: "",
        allowContact: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Submission did not save.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="grid grid-cols-1 gap-4" data-testid="contact-form" onSubmit={handleSubmit}>
      <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="contact-first-name-input" onChange={updateField("firstName")} placeholder="First name" required type="text" value={formData.firstName} />
      <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="contact-last-name-input" onChange={updateField("lastName")} placeholder="Last name" required type="text" value={formData.lastName} />
      <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="contact-phone-input" onChange={updateField("phone")} placeholder="Phone number" required type="tel" value={formData.phone} />
      <input className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="contact-email-input" onChange={updateField("email")} placeholder="Email" required type="email" value={formData.email} />
      <select className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="contact-reason-select" onChange={updateField("reason")} required value={formData.reason}>
        <option disabled value="">Select one</option>
        {CONTACT_REASONS.filter(Boolean).map((reason) => (
          <option key={reason} value={reason}>{reason}</option>
        ))}
      </select>
      <textarea className="min-h-[11rem] border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-slate-950" data-testid="contact-details-textarea" onChange={updateField("details")} placeholder={title} required value={formData.details} />
      <div className="border border-[rgba(10,15,26,0.12)] bg-white px-4 py-4" data-testid="contact-allow-contact-field">
        <p className="text-sm font-semibold text-slate-800">Can we contact you?</p>
        <div className="mt-3 flex flex-wrap gap-6 text-sm text-slate-700">
          <label className="inline-flex items-center gap-2" data-testid="contact-allow-contact-yes-option">
            <input checked={formData.allowContact === "Yes"} name="allow-contact" onChange={updateField("allowContact")} required type="radio" value="Yes" />
            Yes
          </label>
          <label className="inline-flex items-center gap-2" data-testid="contact-allow-contact-no-option">
            <input checked={formData.allowContact === "No"} name="allow-contact" onChange={updateField("allowContact")} required type="radio" value="No" />
            No
          </label>
        </div>
      </div>
      <Button className="rounded-none bg-[#D21404] px-5 py-6 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid="contact-submit-button" disabled={submitting} type="submit">
        {submitting ? "Saving" : submitLabel}
      </Button>
    </form>
  );
};